/**
 * PolylineRenderer - Renders polylines to a 2D canvas
 *
 * Implements ICanvasRenderer for use with the video registry.
 * Draws polyline data as 2D line strokes (XY projection, ignoring Z).
 * Auto-fits content to canvas with padding.
 */

import { BaseRenderer } from '@plugins/video/BaseRenderer';
import type { Polylines } from './TurtleNode';

const DEFAULT_COLOR = '#3b82f6';
const PADDING_RATIO = 0.08;

export class PolylineRenderer extends BaseRenderer {
  private polylines: Polylines = [];
  private ctx: CanvasRenderingContext2D | null = null;
  private dirty = true;
  private strokeColor = DEFAULT_COLOR;

  private cachedScale = 1;
  private cachedCx = 0;
  private cachedCy = 0;
  private cachedPolylineCount = -1;

  setPolylines(polylines: Polylines): void {
    this.polylines = polylines;
    this.dirty = true;
  }

  setColor(color: string): void {
    if (color === this.strokeColor) return;
    this.strokeColor = color;
    this.dirty = true;
  }

  protected initializeRenderer(canvas: HTMLCanvasElement): void {
    this.ctx = canvas.getContext('2d');
    this.cachedPolylineCount = -1;
    this.dirty = true;
  }

  setSize(width: number, height: number): void {
    if (!this.canvas) return;
    this.canvas.width = width;
    this.canvas.height = height;
    this.cachedPolylineCount = -1;
    this.dirty = true;
  }

  protected _render(): void {
    if (!this.ctx || !this.canvas) return;
    if (!this.dirty) return;
    this.dirty = false;

    const ctx = this.ctx;
    const width = this.canvas.width;
    const height = this.canvas.height;

    ctx.clearRect(0, 0, width, height);

    if (this.polylines.length === 0) return;

    if (this.polylines.length !== this.cachedPolylineCount) {
      this.cachedPolylineCount = this.polylines.length;

      let minX = Infinity, minY = Infinity;
      let maxX = -Infinity, maxY = -Infinity;
      for (const line of this.polylines) {
        for (const p of line.points) {
          if (p.x < minX) minX = p.x;
          if (p.x > maxX) maxX = p.x;
          if (p.y < minY) minY = p.y;
          if (p.y > maxY) maxY = p.y;
        }
      }

      const spanX = maxX - minX || 1;
      const spanY = maxY - minY || 1;
      this.cachedCx = (minX + maxX) / 2;
      this.cachedCy = (minY + maxY) / 2;

      const padding = Math.min(width, height) * PADDING_RATIO;
      const drawW = width - padding * 2;
      const drawH = height - padding * 2;
      this.cachedScale = Math.min(drawW / spanX, drawH / spanY);
    }

    const scale = this.cachedScale;
    const cx = this.cachedCx;
    const cy = this.cachedCy;
    const offsetX = width / 2;
    const offsetY = height / 2;

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    for (const line of this.polylines) {
      if (line.points.length < 2) continue;
      ctx.beginPath();
      const p0 = line.points[0];
      ctx.moveTo(offsetX + (p0.x - cx) * scale, offsetY - (p0.y - cy) * scale);
      for (let i = 1; i < line.points.length; i++) {
        const p = line.points[i];
        ctx.lineTo(offsetX + (p.x - cx) * scale, offsetY - (p.y - cy) * scale);
      }
      if (line.fill) {
        ctx.closePath();
        ctx.fillStyle = line.fill;
        ctx.fill();
      }
      if (line.color || !line.fill) {
        ctx.strokeStyle = line.color ?? this.strokeColor;
        ctx.lineWidth = (line.lineWidth ?? 1.5) * Math.min(width, height) / 300;
        ctx.stroke();
      }
    }
  }
}
